import { cn } from '@plato/ui/util/classes';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';
import { FilterOptionsList } from './filter-options-list.tsx';
import type { CheckboxFilterOption } from './types/filter-types.ts';

type CheckboxFilterProps = {
    filter: CheckboxFilterOption;
    onChange: (value: string[]) => void;
    className?: string;
};

export function CheckboxFilter({ filter, onChange, className }: CheckboxFilterProps) {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');

    const onSearchOptions = filter.onSearchOptions;
    const isServerSearch = !!onSearchOptions;

    const handleValuesChange = (newValues: string[]) => {
        const previousValues = filter.selectedValues ?? [];
        for (const value of newValues) {
            if (!previousValues.includes(value)) {
                filter.onSelectOption?.(value);
            }
        }

        if (filter.multiple === false) {
            // Single selection mode - only take the last selected value
            const lastValue = newValues.at(-1);
            onChange(lastValue ? [lastValue] : []);
        } else {
            // Multiple selection mode (default)
            onChange(newValues);
        }
    };

    const handleFilterChange = (value: string): void => {
        setSearchTerm(value);
    };

    useEffect(() => {
        // Reset the server-side search once, when the facet opens.
        onSearchOptions?.('');
        // eslint-disable-next-line react-hooks/exhaustive-deps -- run only on open (mount)
    }, []);

    useDebounce(
        () => {
            if (onSearchOptions) {
                onSearchOptions(searchTerm);
            }
        },
        300,
        [searchTerm, onSearchOptions],
    );

    const filteredOptions = useMemo(() => {
        // Server-backed facets are already filtered by the query, and keep their selected
        // options pinned regardless of the term, so skip the client-side filtering here.
        if (isServerSearch || !searchTerm.trim()) {
            return filter.options;
        }

        return filter.options.filter(
            (option) =>
                option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                option.value.toLowerCase().includes(searchTerm.toLowerCase()),
        );
    }, [filter.options, searchTerm, isServerSearch]);

    return (
        <div className={cn('flex flex-col gap-2', className)}>
            <div className="sticky -top-2 bg-white z-10">
                <FilterOptionsList.Search placeholder={t('search')} onChange={handleFilterChange} />
            </div>
            {filter.isLoadingOptions ? (
                <FilterOptionsList.LoadingSpinner />
            ) : (
                <FilterOptionsList.CheckboxGroup
                    values={filter.selectedValues || []}
                    onChange={handleValuesChange}
                    itemHeight={26 + 8} // 26px height + 8px margin-bottom
                >
                    {filteredOptions.map((option) => (
                        <FilterOptionsList.Checkbox
                            key={option.id}
                            value={option.value}
                            label={option.label}
                            count={option.count}
                        />
                    ))}
                </FilterOptionsList.CheckboxGroup>
            )}
        </div>
    );
}
